import { Avatar, EmptyState, Spinner } from '@/components/ui';
import { useBacklogItems, useProject } from '@/hooks/queries';
import { useProjectStore } from '@/stores/project';
import type { BacklogItem, ProjectRole } from '@/types/api';

const ROLE_LABEL: Record<ProjectRole, string> = {
  PRODUCT_OWNER: 'Product Owner',
  SCRUM_MASTER: 'Scrum Master',
  DEVELOPER: 'Desarrollo',
  VIEWER: 'Observador',
};

/** A partir de cuantos puntos se considera que alguien va cargado de mas. */
const HIGH_LOAD_POINTS = 11;

export function TeamPage() {
  const projectId = useProjectStore((state) => state.activeProjectId);
  const { data: project, isLoading } = useProject(projectId);
  const { data: items } = useBacklogItems(projectId);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <EmptyState title="Elige un proyecto en la barra lateral" />
      </div>
    );
  }

  return (
    <div className="grid min-h-0 flex-1 grid-cols-3 content-start gap-3.5 overflow-auto px-6 py-[22px]">
      {project.members.map((member) => {
        const stats = workload(items ?? [], member.user.id);
        const load = Math.min(100, Math.round((stats.points / HIGH_LOAD_POINTS) * 100));
        const heavy = stats.points >= HIGH_LOAD_POINTS;

        return (
          <article
            key={member.id}
            className="flex flex-col gap-3 rounded-panel border border-line bg-surface p-4"
          >
            <div className="flex items-center gap-[11px]">
              <Avatar user={member.user} size={40} />
              <div className="min-w-0">
                <h2 className="truncate font-display text-[14.5px] font-semibold">
                  {member.user.fullName}
                </h2>
                <p className="truncate text-xs text-subtle">
                  {member.user.jobTitle ?? ROLE_LABEL[member.role]}
                </p>
              </div>
            </div>

            <div className="flex gap-[18px]">
              <div>
                <p className="font-display text-[19px] font-semibold">{stats.open}</p>
                <p className="text-[11.5px] text-subtle">abiertas</p>
              </div>
              <div>
                <p className="font-display text-[19px] font-semibold">{stats.points}</p>
                <p className="text-[11.5px] text-subtle">pts sprint</p>
              </div>
              <div>
                <p className="font-display text-[19px] font-semibold">{stats.done}</p>
                <p className="text-[11.5px] text-subtle">hechas</p>
              </div>
            </div>

            <div
              role="progressbar"
              aria-valuenow={load}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Carga de ${member.user.fullName}`}
              className="h-1.5 overflow-hidden rounded bg-muted-surface"
            >
              <div
                className="h-full rounded"
                style={{ width: `${load}%`, background: heavy ? '#e0a94a' : '#35c48f' }}
              />
            </div>
            <p className="text-[11.5px] text-subtle">
              {heavy ? 'Carga alta para el sprint' : 'Carga saludable'}
            </p>
          </article>
        );
      })}
    </div>
  );
}

/** Trabajo asignado a una persona dentro del sprint en curso. */
function workload(items: BacklogItem[], userId: string) {
  const mine = items.filter((item) => item.assignee?.id === userId);
  const inSprint = mine.filter((item) => item.sprintId !== null);

  return {
    open: inSprint.filter((item) => item.status !== 'DONE').length,
    done: inSprint.filter((item) => item.status === 'DONE').length,
    points: inSprint
      .filter((item) => item.status !== 'DONE')
      .reduce((sum, item) => sum + (item.storyPoints ?? 0), 0),
  };
}
